import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@repo/ui/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current shadow-sm",
  {
    variants: {
      variant: {
        default:
          "bg-primary/5 text-card-foreground border-l-4 border-l-primary",
        destructive:
          "text-destructive bg-destructive/5 border-l-4 border-l-destructive [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90",
        success:
          "bg-emerald-50/80 text-emerald-800 border-emerald-200 border-l-4 border-l-emerald-500 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800 [&>svg]:text-emerald-500 dark:[&>svg]:text-emerald-400",
        warning:
          "bg-amber-50/80 text-amber-800 border-amber-200 border-l-4 border-l-amber-500 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800 [&>svg]:text-amber-500 dark:[&>svg]:text-amber-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
